import { rem } from '@triumph/design-system';
import { Tag } from 'primereact/tag';
import { forwardRef } from 'react';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { useApplicantSubmissionsQuery } from '@/components/shared/api/queries/applicant';
import { TAB_BADGE_PARTIAL_STYLE } from '@/components/shared/constants/conditionsBadge';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import { ApplicantTab, TabBadgeSpinner } from '../Applicant.StyledComponents';
import { ApplicantInfoWrapper } from './ApplicantInfoWrapper';
import { ApplicantSubmissions } from './ApplicantSubmissions';
import {
    SUBMISSIONS_COUNT_ERROR_TITLE,
    SUBMISSIONS_COUNT_STYLE,
    SUBMISSIONS_COUNT_UNKNOWN,
} from './ApplicantSubmissions/constants';

// The tab label and the tab share this query, so the badge costs no extra request.
const useSubmissionsList = () => {
    const { applicantId } = useApplicantContext();
    // Off skips the list call too, so ORP never hits submission routes the gateway may lack.
    const { isSubmissionsTabEnabled } = useFeatureFlags();
    const query = useApplicantSubmissionsQuery({
        applicantId: applicantId ?? '',
        isEnabled: isSubmissionsTabEnabled,
    });

    return { isSubmissionsTabEnabled, ...query };
};

export const ApplicationTabLabel = () => {
    const {
        isSubmissionsTabEnabled,
        data: submissions,
        isLoading,
        isLoadingError,
    } = useSubmissionsList();

    if (!isSubmissionsTabEnabled) return 'Application';

    // Orange like Related Entities when the list fails, with no count, since none is known.
    const badge = isLoadingError
        ? {
              value: SUBMISSIONS_COUNT_UNKNOWN,
              title: SUBMISSIONS_COUNT_ERROR_TITLE,
              style: TAB_BADGE_PARTIAL_STYLE,
          }
        : {
              value: submissions?.length ?? 0,
              title: undefined,
              style: SUBMISSIONS_COUNT_STYLE,
          };

    return (
        <ApplicantTab>
            Application{' '}
            {isLoading ? (
                <TabBadgeSpinner
                    strokeWidth="6"
                    data-testid="applicant-submissions-count-loading"
                />
            ) : (
                <Tag
                    severity="info"
                    value={badge.value}
                    rounded
                    title={badge.title}
                    data-testid="applicant-submissions-count"
                    style={{
                        fontSize: rem(11),
                        padding: `${rem(1)} ${rem(6)}`,
                        ...badge.style,
                    }}
                />
            )}
        </ApplicantTab>
    );
};

// With the flag on, the pulldown always shows; until the list arrives, or when it fails or is
// empty, it offers only the current application, so the live view never waits on it.
export const ApplicationTab = forwardRef<HTMLDivElement>((_, ref) => {
    const { isSubmissionsTabEnabled, data: submissions } = useSubmissionsList();

    if (!isSubmissionsTabEnabled) {
        return <ApplicantInfoWrapper ref={ref} />;
    }

    return <ApplicantSubmissions submissions={submissions} />;
});
